// The combined-H3 vocabulary for the map lab (`/_/map-lab`).
//
// The operator's verdict on the single-dataset grid was "super clean, but it
// breaks with several datasets": every dataset shares ONE tessellation, so the
// same cell is drawn by several layers and the last one wins. This module is
// the answer to that — the vocabulary for the three encodings that put several
// datasets into one cell — and it is a pure module (no Vue, no store, no map)
// so the pane (paint), the legend (key) and the trays (controls, composition)
// all describe the same encoding from one definition instead of three.
//
// Everything here reads the ONE aligned tile: `h3-multi` emits, per
// H3 cell, the cell's own hexagon carrying the cell id and every series' count
// IN that cell (see etc/tegola.toml). A series is therefore never a layer: it
// is a field of the same feature, which is what makes the encoding a repaint
// rather than a re-fetch.
//
// The domains (each series' busiest cell, its terciles, the busiest cell total)
// come from the lab's own count endpoint, counted at the grid's working
// resolution — the same population the tile counts, so a legend's scale and the
// fill it describes cannot disagree.
import type { LabH3Series } from "@/type/api";

import {
	SERIES_ROLES,
	type SeriesMeasureID,
	type SeriesSlot,
	seriesByIndex,
	seriesRoleByID,
	seriesSlotField,
} from "@/lib/h3/series";

/** A drawn series' identity. The four table datasets are the lab's own; `s0`
 * .. `s3` are the SERIES SLOTS — the operator's predicates, one per role — and
 * they are separate ids on purpose: a slot is not a table, and a mode that
 * drew the slot carrying "chemical" must not be labelled with the table it
 * came from. */
export type MultiSeriesID =
	| "treatment"
	| "observation"
	| "feature"
	| "site"
	| "s0"
	| "s1"
	| "s2"
	| "s3";

/** One series in the combined view. `field` is the MVT property its per-cell
 * value arrives in; the tile carries every drawn series on every feature, so a
 * mode that draws two and a mode that draws four are the same bytes. */
export interface MultiSeries {
	id: MultiSeriesID;
	label: string;
	/** The single line the tray shows as this series' source. */
	table: string;
	/** Identity hue: the bivariate/diff axis hue. */
	hue: string;
	/** 5-stop sequential ramp, light → dark, for that series' own intensity. */
	ramp: string[];
	field: string;
}

export const MULTI_SERIES: MultiSeries[] = [
	{
		id: "treatment",
		label: "Treatments",
		table: "public.treatment",
		hue: "#16a34a",
		ramp: ["#f0fdf4", "#bbf7d0", "#4ade80", "#16a34a", "#14532d"],
		field: "n_treatment",
	},
	{
		id: "observation",
		label: "Inspections (observations)",
		table: "public.observation ⋈ feature",
		hue: "#dc2626",
		ramp: ["#fff7ed", "#fed7aa", "#fb923c", "#ea580c", "#7c2d12"],
		field: "n_observation",
	},
	{
		id: "feature",
		label: "Features",
		table: "public.feature",
		hue: "#0d9488",
		ramp: ["#f0fdfa", "#99f6e4", "#2dd4bf", "#0d9488", "#134e4a"],
		field: "n_feature",
	},
	{
		id: "site",
		label: "Sites",
		table: "public.site",
		hue: "#64748b",
		ramp: ["#f8fafc", "#e2e8f0", "#94a3b8", "#475569", "#1e293b"],
		field: "n_site",
	},
];

export const MULTI_SERIES_IDS: MultiSeriesID[] = MULTI_SERIES.map(
	(series) => series.id,
);

export function multiSeriesByID(id: MultiSeriesID): MultiSeries {
	return MULTI_SERIES.find((series) => series.id === id) ?? MULTI_SERIES[0];
}

/** The four SERIES SLOTS' ids, in role order — the drawn series of a lab whose
 * operator has assigned series rather than ticked tables. */
export const SERIES_SLOT_IDS: MultiSeriesID[] = ["s0", "s1", "s2", "s3"];

/** The descriptor for one assigned series: the role's identity colour and ramp,
 * the operator's own label, and the field the ACTIVE MEASURE reads — the count
 * always rides `n_s{slot}` and the measure's sum `m_s{slot}`, so a measure change
 * re-points the same encoding at another column of the same tile rather than at
 * another tile.
 *
 * The ramp stays the ROLE's ramp even when the operator recolours the swatch:
 * the colour is the series' identity in the key, while the ramp is the scale a
 * value is drawn on, and two series whose ramps were derived from two arbitrary
 * colours would stop being comparable cell to cell. */
export function slotSeries(
	slot: SeriesSlot,
	measure: SeriesMeasureID,
): MultiSeries {
	const role = seriesRoleByID(slot.role);
	return {
		id: `s${role.slot}` as MultiSeriesID,
		label: slot.label,
		table: seriesByIndex(slot.index).table,
		hue: slot.color,
		ramp: role.ramp,
		field: seriesSlotField(role.slot, measure),
	};
}

/** True for a table dataset id or a series slot id — everything the combined
 * encodings can be asked to draw. */
export function isMultiSeriesID(value: string): value is MultiSeriesID {
	return [...MULTI_SERIES_IDS, ...SERIES_SLOT_IDS].includes(
		value as MultiSeriesID,
	);
}

/** The single-ramp look the operator liked, for the total in `hex-breakdown`:
 * one hue, light → dark, no identity encoding at all. */
export const MULTI_TOTAL_RAMP = [
	"#eff6ff",
	"#bfdbfe",
	"#93c5fd",
	"#3b82f6",
	"#1d4ed8",
	"#1e3a8a",
];

/** tegola names, kept here so the pane and the URL builder cannot drift. */
export const MULTI_MAP = "h3-multi";
export const MULTI_SOURCE_LAYER = "service-area-h3-multi";
export const MULTI_PARAM_RESOLUTION = "h3_multi_resolution";
export const MULTI_PARAM_FROM = "h3_multi_from";
export const MULTI_PARAM_TO = "h3_multi_to";

/** Two datasets is the floor for a combination (there is nothing to combine
 * below it) and the tile's four series the ceiling. */
export const MULTI_MIN_SERIES = 2;
export const MULTI_MAX_SERIES = MULTI_SERIES.length;

/** The one line the tray's mode rows show for a combined encoding: the tile the
 * mode really reads and the datasets inside it, so a multi-dataset mode cannot
 * be labelled with one table. */
export function multiSourceLine(series: MultiSeriesID[]): string {
	const labels = series.map((id) => multiSeriesByID(id).label);
	return `${MULTI_MAP} · ${labels.join(" + ")}`;
}

/**
 * The combined grid's tile URL template for one resolution and window.
 *
 * It is pure and — the point — it takes no encoding: every combined mode reads
 * this same template, so an encoding switch hands MapLibre the same source id
 * and the same strings. MapLibre's `setTiles` no-ops on an equal URL array,
 * which is what makes the switch a repaint rather than a re-request. A series,
 * a pair or a spotlight that found its way into this URL would fork the source
 * and re-fetch the tile, so this signature is load-bearing.
 *
 * Both window bounds are optional and an absent one means unbounded to the
 * server, so the standing datasets' panes (which never set a window) send the
 * same template with no date parameters at all.
 */
export function multiTileTiles(input: {
	base: string;
	resolution: number;
	from: string;
	to: string;
}): string[] {
	if (!input.base) return [];
	const params = new URLSearchParams();
	params.set(MULTI_PARAM_RESOLUTION, String(input.resolution));
	if (input.from) params.set(MULTI_PARAM_FROM, input.from);
	if (input.to) params.set(MULTI_PARAM_TO, input.to);
	return [`${input.base}maps/${MULTI_MAP}/{z}/{x}/{y}?${params}`];
}

// ── Domains ────────────────────────────────────────────────────────────────

export interface MultiSeriesDomain {
	total: number;
	cells: number;
	maxCellCount: number;
	/** Terciles of the series' per-cell counts over the cells it appears in —
	 * the bivariate classes' real thresholds. */
	p33: number;
	p67: number;
}

export type MultiDomains = Partial<Record<string, MultiSeriesDomain>>;

export function domainsFromSeries(
	series: LabH3Series[] | undefined,
): MultiDomains {
	const domains: MultiDomains = {};
	for (const entry of series ?? []) {
		if (!isMultiSeriesID(entry.id)) continue;
		domains[entry.id] = {
			total: entry.total,
			cells: entry.cells,
			maxCellCount: entry.maxCellCount,
			p33: entry.p33,
			p67: entry.p67,
		};
	}
	return domains;
}

/** Everything a combined encoding needs to build its paint and its key.
 *
 * `series` holds resolved DESCRIPTORS rather than ids because a series slot's
 * label, colour and field are the operator's, not the module's: the encode is
 * where the assignment becomes an encoding, and every painter, legend and
 * readout reads it from here rather than looking an id up in a fixed table that
 * cannot know about slots. */
export interface MultiEncode {
	/** The series drawn, in draw order (the slots in role order, or the four
	 * tables). */
	series: MultiSeries[];
	domains: MultiDomains;
	/** The series being read through the combined view, or null for all. */
	spotlight: string | null;
	/** The busiest cell's TOTAL across every drawn series, which is the breakdown
	 * ramp's domain — a sum of per-series maxima would not bound a sum. */
	maxCellTotal: number;
}

/** The drawn series a mode reads as its two axes. Fewer than two drawn series
 * (the standing selection has at least two; an operator who assigned only Fill A
 * has one) falls back to the tables so a paint expression is always well
 * defined, and the legend says which series are really drawn. */
export function axisPair(encode: MultiEncode): [MultiSeries, MultiSeries] {
	return [
		encode.series[0] ?? MULTI_SERIES[0],
		encode.series[1] ?? MULTI_SERIES[1],
	];
}

/** The wash the sequential and diverging fills draw at; the same 0.75-ish
 * single-dataset wash the density grid uses, so the comparison against it stays
 * fair. The bivariate is deliberately NOT drawn at this wash — see the fill's
 * own note in the pane. */
export const MULTI_FILL_OPACITY = 0.8;

// ── Paint expressions ──────────────────────────────────────────────────────
//
// All of them are MapLibre expressions over the tile's own properties, so
// changing the encoding — a different pair, a spotlight, a different scale — is
// a style update on the same tiles.

const TRANSPARENT = "rgba(0,0,0,0)";

/** `count / max`, floored at 1 so an empty domain (still counting) draws full
 * intensity rather than dividing by zero. */
function scaledCount(field: string, max: number): unknown[] {
	return ["/", ["get", field], Math.max(1, max)];
}

/** A perceptual spread of a heavily skewed count: the square root of the
 * normalised count. A cell with 1 of the busiest 900 would otherwise be
 * indistinguishable from an empty one; with the root it lands at a tenth of the
 * ramp instead of at nothing. */
function spread(value: unknown[]): unknown[] {
	return ["^", value, 0.5];
}

/** The series' own ramp, interpolated on the spread normalised count. */
function rampExpression(field: string, max: number, ramp: string[]): unknown[] {
	const stops = Math.max(1, ramp.length - 1);
	const expression: unknown[] = [
		"interpolate",
		["linear"],
		spread(scaledCount(field, max)),
	];
	ramp.forEach((color, index) => {
		expression.push(index / stops, color);
	});
	return expression;
}

// ── hex-bivariate: 3×3 of two datasets ─────────────────────────────────────

function classExpression(
	field: string,
	domain: MultiSeriesDomain | undefined,
): unknown[] {
	const p33 = Math.max(0, domain?.p33 ?? 0);
	const p67 = Math.max(p33, domain?.p67 ?? 0);
	return [
		"case",
		["==", ["get", field], 0],
		0,
		["<=", ["get", field], p33],
		0,
		["<=", ["get", field], p67],
		1,
		2,
	];
}

/**
 * The 3×3 bivariate palette, `grid[a][b]` with `a` the first dataset's intensity
 * class rising upwards and `b` the second's running left to right: bottom-left
 * is neither dataset, top-right both. It is a constant rather than a mix of the
 * datasets' own hues on purpose — the two axes are always the SAME blue and
 * orange, so a pair is legible whoever the datasets are, and the red/green hues
 * two of the district's datasets happen to wear are never asked to do the work
 * (they are the pair a red-green colour deficiency collapses).
 *
 * Construction: a very light neutral base (#eef1f5), each axis a light → strong
 * tint of its hue mixed additively into that base, and the four interior cells
 * (both datasets present) the same mix with its chroma lifted so the mixture
 * reads as a colour rather than as grey. Column 0 is therefore the first axis'
 * own ramp and row 0 the second's — which is exactly what the legend's axis
 * strips draw, so the key cannot describe a scale the fill does not use.
 *
 * Measured (CIEDE2000, this file's nine cells, see the report): minimum
 * pairwise and minimum adjacent-cell ΔE are both 16.2, against 0.0 for the
 * predecessor — whose subtractive mix of each dataset's darkest ramp stop
 * collapsed three of its nine classes to #000000, i.e. three classes the
 * operator could not tell apart at all. Under the Machado severity-1.0
 * simulations the minimum adjacent ΔE is 15.0 (protanopia), 15.6
 * (deuteranopia) and 12.2 (tritanopia). Lightness rises from L* 95 at
 * bottom-left to L* 34 at top-right, with the four interior cells at
 * L* 60–81 — the middle of the grid is the light end, which is what the
 * operator asked for ("too close and dark").
 */
export const BIVARIATE_GRID: string[][] = [
	["#eef1f5", "#ddc5af", "#ca925e"],
	["#b2bad7", "#a78b8f", "#a4582e"],
	["#6c7cb5", "#5a4878", "#754144"],
];

/**
 * The bivariate fill: both datasets' intensity classes in one colour per cell.
 * The classes come from the window's own terciles, so a skewed dataset still
 * spreads across three bands instead of collapsing into the lowest one.
 *
 * A spotlight does not dim half a bivariate cell — it reads the spotlit dataset
 * *alone* (its own sequential ramp), which is the "see one dataset cleanly"
 * the control promises.
 */
export function bivariateFillColor(encode: MultiEncode): unknown[] {
	const [a, b] = axisPair(encode);
	if (encode.spotlight === a.id) {
		return rampExpression(
			a.field,
			encode.domains[a.id]?.maxCellCount ?? 0,
			a.ramp,
		);
	}
	if (encode.spotlight === b.id) {
		return rampExpression(
			b.field,
			encode.domains[b.id]?.maxCellCount ?? 0,
			b.ramp,
		);
	}
	const grid = BIVARIATE_GRID;
	// `let` binds each class once so the nine-way case does not re-evaluate the
	// two class ladders nine times per feature.
	return [
		"let",
		"ca",
		classExpression(a.field, encode.domains[a.id]),
		[
			"let",
			"cb",
			classExpression(b.field, encode.domains[b.id]),
			[
				"case",
				["==", ["var", "ca"], 2],
				[
					"case",
					["==", ["var", "cb"], 2],
					grid[2][2],
					["==", ["var", "cb"], 1],
					grid[2][1],
					grid[2][0],
				],
				["==", ["var", "ca"], 1],
				[
					"case",
					["==", ["var", "cb"], 2],
					grid[1][2],
					["==", ["var", "cb"], 1],
					grid[1][1],
					grid[1][0],
				],
				[
					"case",
					["==", ["var", "cb"], 2],
					grid[0][2],
					["==", ["var", "cb"], 1],
					grid[0][1],
					grid[0][0],
				],
			],
		],
	];
}

// ── hex-diff: diverging dominance ──────────────────────────────────────────

/** The two ends of the diverging scale, and the neutral middle it crosses. */
export const MULTI_DIVERGING_NEUTRAL = "#f8fafc";

/** The diverging strip, b-end → equal → a-end, mirrored around the neutral. */
export function divergingStops(a: MultiSeries, b: MultiSeries): string[] {
	return [
		b.ramp[4],
		b.ramp[3],
		b.ramp[2],
		MULTI_DIVERGING_NEUTRAL,
		a.ramp[2],
		a.ramp[3],
		a.ramp[4],
	];
}

/**
 * The difference fill: the first dataset's SHARE of the pair, diverging from
 * "all B" through the neutral at an even split to "all A". Absolute counts are
 * deliberately not the axis — a cell with 2 of A and 1 of B is as interesting as
 * one with 200 and 100, and only the share says so.
 */
export function diffFillColor(encode: MultiEncode): unknown[] {
	const [a, b] = axisPair(encode);
	const stops = divergingStops(a, b);
	const total = ["+", ["get", a.field], ["get", b.field]];
	const share = ["/", ["get", a.field], ["var", "share_total"]];
	const expression: unknown[] = [
		"interpolate",
		["linear"],
		share,
		0,
		stops[0],
		0.25,
		stops[1],
		0.5,
		stops[3],
		0.75,
		stops[5],
		1,
		stops[6],
	];
	return [
		"let",
		"share_total",
		total,
		// A cell with neither dataset (possible in principle, impossible in the
		// tile) has no share to draw.
		["case", ["==", ["var", "share_total"], 0], TRANSPARENT, expression],
	];
}

// ── hex-breakdown: total, with composition on demand ───────────────────────

/** The drawn series' values summed, as one expression over the tile. */
function totalExpression(series: MultiSeries[]): unknown {
	const terms = series.map((entry) => ["get", entry.field]);
	return terms.reduce<unknown>(
		(sum, term, index) => (index === 0 ? term : ["+", sum, term]),
		0,
	);
}

/**
 * The breakdown fill: the window-total activity per cell in ONE sequential ramp
 * — exactly the single-dataset grid's look — normalised by the busiest cell's
 * total, so the ramp is comparable cell to cell and across datasets.
 */
export function breakdownFillColor(encode: MultiEncode): unknown[] {
	const ramp = MULTI_TOTAL_RAMP;
	const stops = ramp.length - 1;
	const expression: unknown[] = [
		"interpolate",
		["linear"],
		spread([
			"/",
			totalExpression(encode.series),
			Math.max(1, encode.maxCellTotal),
		]),
	];
	ramp.forEach((color, index) => {
		expression.push(index / stops, color);
	});
	return expression;
}

/** The total the breakdown fill draws for one cell — the rail's headline and the
 * hover label's number. */
export function cellTotal(counts: Partial<Record<string, number>>): number {
	return Object.values(counts).reduce<number>(
		(sum, count) => sum + (count ?? 0),
		0,
	);
}

// ── Legends ────────────────────────────────────────────────────────────────

export interface BivariateAxis {
	id: string;
	label: string;
	hue: string;
	/** The axis' own three cells — the grid's edge that this axis produces, so
	 * the key's strip is literally the scale the fill paints with. */
	stops: string[];
	/** The class thresholds the fill really uses (terciles of the non-zero
	 * cells), so the key's numbers cannot drift from the map's bands. */
	thresholds: [number, number];
}

export interface BivariateLegend {
	/** `grid[a][b]`; a rises upwards, b to the right. */
	grid: string[][];
	a: BivariateAxis;
	b: BivariateAxis;
}

export function bivariateLegend(encode: MultiEncode): BivariateLegend {
	const [a, b] = axisPair(encode);
	const axis = (series: MultiSeries, stops: string[]): BivariateAxis => {
		const domain = encode.domains[series.id];
		return {
			id: series.id,
			label: series.label,
			hue: series.hue,
			stops,
			thresholds: [domain?.p33 ?? 0, domain?.p67 ?? 0],
		};
	};
	return {
		grid: BIVARIATE_GRID,
		a: axis(
			a,
			BIVARIATE_GRID.map((row) => row[0]),
		),
		b: axis(b, BIVARIATE_GRID[0]),
	};
}

export interface DivergingLegend {
	stops: string[];
	a: { id: string; label: string; hue: string };
	b: { id: string; label: string; hue: string };
}

export function divergingLegend(encode: MultiEncode): DivergingLegend {
	const [a, b] = axisPair(encode);
	return {
		stops: divergingStops(a, b),
		a: { id: a.id, label: a.label, hue: a.hue },
		b: { id: b.id, label: b.label, hue: b.hue },
	};
}

export interface CompositionRow {
	id: string;
	label: string;
	hue: string;
	count: number;
	/** This series' share of the window (legend) or of the cell (rail). */
	share: number;
}

/** One series' place in a composition, `share` in 0..1. */
export function compositionRows(
	counts: Partial<Record<string, number>>,
	series: MultiSeries[],
): CompositionRow[] {
	const total = series.reduce((sum, entry) => sum + (counts[entry.id] ?? 0), 0);
	return series.map((entry) => {
		const count = counts[entry.id] ?? 0;
		return {
			id: entry.id,
			label: entry.label,
			hue: entry.hue,
			count,
			share: total > 0 ? count / total : 0,
		};
	});
}

export interface BreakdownLegend {
	ramp: string[];
	rows: CompositionRow[];
}

export function breakdownLegend(encode: MultiEncode): BreakdownLegend {
	const totals: Partial<Record<string, number>> = {};
	for (const entry of encode.series)
		totals[entry.id] = encode.domains[entry.id]?.total ?? 0;
	return {
		ramp: MULTI_TOTAL_RAMP,
		rows: compositionRows(totals, encode.series),
	};
}

/** The MapReviewSpec the ledger itself uses to filter by a hexagon — shown by the
 * rail beside a picked cell so the operator can see the filter the grid's
 * drill-down would send. It is formatted, not sent: the lab has no path into
 * the workbench's spec state, and inventing one would reach into the ledger. */
export function cellSpecText(cell: string, resolution: number): string {
	return JSON.stringify({ h3: { cell, resolution } });
}

/** The drawn series' values for one cell, keyed by series id, for the hit-test
 * that reads a picked cell's composition out of an MVT feature's properties.
 * The property names come from the descriptors, so a series slot is read from
 * the column its measure named (`n_s2` for records, `m_s2` for a sum) rather
 * than from a table field the slot does not have. */
export function cellCounts(
	properties: Record<string, unknown> | null | undefined,
	series: MultiSeries[],
): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const entry of series) counts[entry.id] = 0;
	if (!properties) return counts;
	for (const entry of series) {
		const value = properties[entry.field];
		counts[entry.id] = typeof value === "number" ? value : 0;
	}
	return counts;
}
